import React, { useEffect, useState } from 'react';
import { PageLayout } from '../components/PageLayout';
import {
  getRetentionPreference,
  updateRetentionPreference,
  exportUserData,
  ApiRequestError,
} from '../services/api';

export function Settings() {
  const [retainData, setRetainData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchRetentionPreference();
  }, []);

  function toErrorMessage(err: unknown, fallback: string): string {
    if (err instanceof ApiRequestError) return err.message;
    if (err instanceof Error) return err.message;
    return fallback;
  }

  async function fetchRetentionPreference() {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getRetentionPreference();
      setRetainData(data.retainData ?? false);
    } catch (err) {
      setError(toErrorMessage(err, 'Failed to load retention settings'));
    } finally {
      setIsLoading(false);
    }
  }

  async function handleRetentionToggle() {
    const newValue = !retainData;
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await updateRetentionPreference(newValue);
      setRetainData(newValue);
      setSuccessMessage(
        newValue
          ? 'Data retention enabled. Your audit reports will be stored for up to 24 months.'
          : 'Data retention disabled. Existing retained reports will be deleted.'
      );
    } catch (err) {
      setError(toErrorMessage(err, 'Failed to update retention setting'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDataExport() {
    setIsExporting(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const blob = await exportUserData();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'auditmypay-data-export.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccessMessage('Data export downloaded successfully.');
    } catch (err) {
      setError(toErrorMessage(err, 'Failed to initiate data export'));
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <PageLayout>
      <main className="settings-page" role="main" aria-labelledby="settings-heading">
        <h2 id="settings-heading">Settings</h2>

        {isLoading && (
          <div className="loading" aria-live="polite" role="status">
            Loading settings...
          </div>
        )}

        {error && (
          <div className="error-message" role="alert" aria-live="assertive">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="success-message" role="status" aria-live="polite">
            {successMessage}
          </div>
        )}

        {!isLoading && (
          <>
            <section className="settings-section" aria-labelledby="retention-heading">
              <h3 id="retention-heading">Data Retention</h3>
              <p className="setting-description">
                When enabled, your audit reports are stored for up to 24 months so you
                can review past pay periods. When disabled, all data is deleted
                immediately after analysis.
              </p>
              <div className="toggle-container">
                <label htmlFor="retention-toggle" className="toggle-label">
                  Retain audit reports
                </label>
                <button
                  id="retention-toggle"
                  role="switch"
                  aria-checked={retainData}
                  onClick={handleRetentionToggle}
                  disabled={isSaving}
                  className={`toggle-button ${retainData ? 'toggle-on' : 'toggle-off'}`}
                  aria-label={retainData ? 'Disable data retention' : 'Enable data retention'}
                >
                  {isSaving ? 'Saving...' : retainData ? 'On' : 'Off'}
                </button>
              </div>
            </section>

            <section className="settings-section" aria-labelledby="export-heading">
              <h3 id="export-heading">Data Export</h3>
              <p className="setting-description">
                Download a copy of all your stored data in a machine-readable JSON
                format, in accordance with your data access rights.
              </p>
              <button
                onClick={handleDataExport}
                disabled={isExporting}
                className="export-button"
                aria-label="Export all stored data as JSON"
              >
                {isExporting ? 'Exporting...' : 'Export My Data'}
              </button>
            </section>
          </>
        )}
      </main>
    </PageLayout>
  );
}
